import { useMemo } from 'react';
import { Image } from '@heroui/react';
import { FaCircleExclamation, FaCheck } from 'react-icons/fa6';
import { formatCurrency } from '../../../../utils/currency';
import fallbackImage from '../../../../../public/fallback-image.png';

export interface SessionAddOnCandidate {
  id: string;
  serviceItem: string;
  price: number;
  images?: string;
}

export type SessionAddOnSelection = Record<string, number[]>;

interface SessionAddOnSelectorProps {
  addons: SessionAddOnCandidate[];
  sessionCount: number;
  value: SessionAddOnSelection;
  onChange: (value: SessionAddOnSelection) => void;
  errors?: Record<string, string>;
}

export const getSessionAddOnValidationErrors = (
  addons: SessionAddOnCandidate[],
  value: SessionAddOnSelection
): Record<string, string> => {
  const errors: Record<string, string> = {};
  Object.keys(value).forEach((addonId) => {
    const sessions = value[addonId] || [];
    if (sessions.length === 0) {
      const addon = addons.find((a) => a.id === addonId);
      errors[addonId] = `Select which session${
        (addon ? 1 : 2) > 1 ? '(s)' : ''
      } should include "${addon?.serviceItem || 'this add-on'}" before continuing.`;
    }
  });
  return errors;
};

export const calculateSessionAddOnsSubtotal = (
  addons: SessionAddOnCandidate[],
  value: SessionAddOnSelection
): number => {
  const priceById = new Map(addons.map((a) => [a.id, a.price || 0]));
  return Object.entries(value).reduce((sum, [addonId, sessions]) => {
    const price = priceById.get(addonId) || 0;
    return sum + price * (sessions?.length || 0);
  }, 0);
};

export const toSessionAddOnsPayload = (
  value: SessionAddOnSelection
): Array<{ additionalServiceId: string; sessionNumbers: number[] }> =>
  Object.entries(value)
    .filter(([, sessions]) => (sessions?.length || 0) > 0)
    .map(([additionalServiceId, sessionNumbers]) => ({
      additionalServiceId,
      sessionNumbers: [...sessionNumbers].sort((a, b) => a - b),
    }));

const SessionAddOnSelector = ({
  addons,
  sessionCount,
  value,
  onChange,
  errors = {},
}: SessionAddOnSelectorProps) => {
  const sessionNumbers = useMemo(
    () => Array.from({ length: Math.max(0, sessionCount) }, (_, i) => i + 1),
    [sessionCount]
  );

  if (!addons || addons.length === 0) return null;

  const isChecked = (addonId: string) =>
    Object.prototype.hasOwnProperty.call(value, addonId);

  const toggleAddon = (addonId: string) => {
    const next = { ...value };
    if (!isChecked(addonId)) {
      next[addonId] = [];
    } else {
      delete next[addonId];
    }
    onChange(next);
  };

  const toggleSession = (addonId: string, sessionNumber: number) => {
    const current = value[addonId] || [];
    const next = current.includes(sessionNumber)
      ? current.filter((n) => n !== sessionNumber)
      : [...current, sessionNumber];
    onChange({ ...value, [addonId]: next });
  };

  const toggleAll = (addonId: string) => {
    const current = value[addonId] || [];
    const allSelected = sessionNumbers.every((n) => current.includes(n));
    onChange({ ...value, [addonId]: allSelected ? [] : [...sessionNumbers] });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {addons.map((addon) => {
        const checked = isChecked(addon.id);
        const selectedSessions = value[addon.id] || [];
        const allSelected =
          sessionNumbers.length > 0 &&
          sessionNumbers.every((n) => selectedSessions.includes(n));
        const error = errors[addon.id];

        return (
          <div
            key={addon.id}
            onClick={() => toggleAddon(addon.id)}
            className={`relative p-3 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col justify-between ${
              error
                ? 'border-red-400 bg-red-50/30'
                : checked
                  ? 'border-primary ring-2 ring-primary/10 bg-blue-50/20 shadow-md'
                  : 'border-slate-200 bg-white hover:border-primary/30 hover:shadow-lg'
            }`}
          >
            {/* Top Selection Indicator Badge */}
            {checked && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                <FaCheck size={10} className="text-white" />
              </div>
            )}

            {/* Header info */}
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 bg-slate-100 rounded-xl overflow-hidden shrink-0 border border-slate-200/50">
                <Image
                  src={addon.images || fallbackImage}
                  alt={addon.serviceItem}
                  className="object-cover w-full h-full"
                  radius="none"
                  removeWrapper
                />
              </div>

              <div className="flex-1 min-w-0 pr-6">
                <h4 className="font-semibold text-sm text-slate-800 truncate">
                  {addon.serviceItem}
                </h4>
                <p className="text-xs font-semibold text-blue-600 mt-0.5">
                  {formatCurrency(addon.price)}{' '}
                  <span className="font-normal text-slate-400">
                    / per appointment
                  </span>
                </p>
              </div>
            </div>

            {/* Session selector controls */}
            {checked && (
              <div
                className="mt-4 pt-3 border-t border-slate-200/60"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-600">
                    Apply to which appointment(s)?
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleAll(addon.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                      allSelected
                        ? 'bg-secondary text-white shadow-xs'
                        : 'bg-secondary/20 text-secondary hover:bg-slate-200'
                    }`}
                  >
                    Select All
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sessionNumbers.map((n) => {
                    const active = selectedSessions.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleSession(addon.id, n)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                          active
                            ? 'bg-primary text-white shadow-xs'
                            : 'bg-slate-100 border text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Appointment {n}
                      </button>
                    );
                  })}
                </div>

                {/* Subtotal or Error Display */}
                {selectedSessions.length > 0 ? (
                  <p className="text-xs text-slate-500 mt-2.5">
                    {formatCurrency(addon.price)} × {selectedSessions.length}{' '}
                    appointment{selectedSessions.length > 1 ? 's' : ''} ={' '}
                    <span className="font-bold text-slate-800">
                      {formatCurrency(
                        (addon.price || 0) * selectedSessions.length
                      )}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-red-600 flex items-center gap-1 mt-2.5 font-medium">
                    <FaCircleExclamation size={12} />
                    {error || 'Select at least one session.'}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SessionAddOnSelector;
